import type { AddressUsage } from '~/api/DeliveryAddress';
import { company } from '~/company/Company';
import { ModalType } from '~/components/Modal/interface';
import { userStore } from '~/stores';

import { resolveAddressUsage } from './addressUsage';

/** Query param carrying the address type a modal opened via {@link getAddressModalParams} should show or create. */
export const ADDRESS_USAGE_PARAM = 'address-usage';

/** Query param marking a modal opened to create a new address rather than to change the current one. */
export const ADDRESS_INTENT_PARAM = 'address-intent';

/** Value of {@link ADDRESS_INTENT_PARAM} set by every "add a new address" entry point. */
export const NEW_ADDRESS_INTENT = 'new';

/** Params that open an address modal on a blank form instead of the current address. */
export const NEW_ADDRESS_INTENT_PARAMS = {
  [ADDRESS_INTENT_PARAM]: NEW_ADDRESS_INTENT,
} as const satisfies Record<string, string>;

/**
 * Whether the modal should open blank: the caller asked for a new address AND
 * the tenant's search field can actually be typed in.
 *
 * Tenants without `useAutocompleteAddress` render that field read-only, where
 * it is the only label telling the customer which address the pin sits on —
 * blanking it there would take away the one hint they have, and the
 * concatenation bug it fixes cannot happen in a field nobody can type into.
 */
export const getIsBlankAddressStart = (
  searchParams: URLSearchParams,
): boolean =>
  searchParams.get(ADDRESS_INTENT_PARAM) === NEW_ADDRESS_INTENT &&
  !!company.config.useAutocompleteAddress;

export const getAddressModalType = (): ModalType => {
  if (company.showNewAddressModal) {
    return userStore.addressList.length > 0
      ? ModalType.DeliveryAddressList
      : ModalType.EditAddressLocation;
  }
  return ModalType.DeliveryAddress;
};

/**
 * Single source of truth for "open the list of saved addresses, or open the
 * add-new-address form" — every caller that needs this decision goes through
 * here instead of re-checking `userStore.addressList` itself.
 *
 * `usage` narrows the decision to one delivery type (and is threaded through
 * to the target modal via `ADDRESS_USAGE_PARAM`, so a list modal can filter
 * and a new-address modal can default to the right mode). Omitted, it
 * considers all saved addresses — the behaviour every existing caller
 * already relies on.
 */
export const getAddressModalParams = (
  source: string,
  params?: Record<string, string>,
  usage?: AddressUsage,
): [ModalType, string, Record<string, string> | undefined] => {
  const relevantAddresses = usage
    ? userStore.addressList.filter(
        (address) => resolveAddressUsage(address.usage) === usage,
      )
    : userStore.addressList;

  const targetModal = company.showNewAddressModal
    ? relevantAddresses.length > 0
      ? ModalType.DeliveryAddressList
      : ModalType.EditAddressLocation
    : ModalType.DeliveryAddress;

  const usageParams: Record<string, string> | undefined = usage
    ? { [ADDRESS_USAGE_PARAM]: usage }
    : undefined;

  if (company.showNewAddressModal) {
    return [targetModal, source, usageParams];
  }

  const extraParams = params ? { ...params, ...usageParams } : usageParams;
  return [targetModal, source, extraParams];
};
